/**
 * YAML parsing and serialization utilities for pipeline configurations
 */
import yaml from 'js-yaml';

export interface ParseResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Safely parse YAML string with detailed error handling
 */
export function parseYamlSafely<T = unknown>(yamlText: string): ParseResult<T> {
  try {
    if (!yamlText || yamlText.trim() === '') {
      return {
        success: false,
        error: 'YAML text is empty or undefined',
      };
    }

    const data = yaml.load(yamlText, {
      // Safe mode - only load standard YAML tags
      schema: yaml.DEFAULT_SCHEMA,
      // Don't allow duplicate keys
      json: true,
    }) as T;

    // Check if result is null (empty document)
    if (data === null) {
      return {
        success: false,
        error: 'YAML document is empty or null',
      };
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    let errorMessage = 'Unknown YAML parsing error';

    if (
      error &&
      typeof error === 'object' &&
      'name' in error &&
      error.name === 'YAMLException'
    ) {
      // Extract useful information from YAML error
      const yamlError = error as unknown as {
        message: string;
        mark?: { line: number; column: number };
      };
      errorMessage = `YAML syntax error: ${yamlError.message}`;
      if (yamlError.mark) {
        errorMessage += ` at line ${yamlError.mark.line + 1}, column ${yamlError.mark.column + 1}`;
      }
    } else if (error instanceof Error) {
      errorMessage = `Parsing error: ${error.message}`;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Serialize data to YAML with consistent formatting
 */
export function stringifyYaml(data: unknown): string {
  try {
    return yaml.dump(data, {
      // Use 2-space indentation
      indent: 2,
      // Don't wrap lines
      lineWidth: -1,
      // Don't quote strings unnecessarily
      quotingType: '"',
      // Sort object keys for consistency
      sortKeys: false,
      // Use flow style for arrays in some cases
      flowLevel: -1,
      // Include document start marker
      noRefs: true,
      // Use literal style for multi-line strings
      styles: {
        '!!str': 'literal',
      },
    });
  } catch (error) {
    throw new Error(
      `Failed to serialize to YAML: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Validate basic YAML structure before schema validation
 */
export function validateYamlStructure(data: unknown): ParseResult<unknown> {
  if (typeof data !== 'object' || data === null) {
    return {
      success: false,
      error: 'YAML document must be an object',
    };
  }

  // Check for required top-level properties
  if (!Object.prototype.hasOwnProperty.call(data, 'blocks')) {
    return {
      success: false,
      error: 'Missing required "blocks" property',
    };
  }

  if (!Object.prototype.hasOwnProperty.call(data, 'connections')) {
    return {
      success: false,
      error: 'Missing required "connections" property',
    };
  }

  const dataObj = data as Record<string, unknown>;

  // Validate that blocks is an object
  if (
    typeof dataObj.blocks !== 'object' ||
    dataObj.blocks === null ||
    Array.isArray(dataObj.blocks)
  ) {
    return {
      success: false,
      error: '"blocks" must be an object with block IDs as keys',
    };
  }

  // Validate that connections is an array
  if (!Array.isArray(dataObj.connections)) {
    return {
      success: false,
      error: '"connections" must be an array',
    };
  }

  // Validate blocks structure (blocks is now an object with block IDs as keys)
  const blocksObj = dataObj.blocks as Record<string, unknown>;
  for (const [blockId, blockUnknown] of Object.entries(blocksObj)) {
    if (typeof blockUnknown !== 'object' || blockUnknown === null) {
      return {
        success: false,
        error: `Block "${blockId}" must be an object`,
      };
    }

    // Cast to a record type for property access
    const block = blockUnknown as Record<string, unknown>;

    // Auto-add the id property if it's missing (use the key as the id)
    if (!('id' in block) || !block.id) {
      block.id = blockId;
    } else if (typeof block.id !== 'string') {
      return {
        success: false,
        error: `Block "${blockId}" must have a string "id" property`,
      };
    } else if (block.id !== blockId) {
      return {
        success: false,
        error: `Block "${blockId}" has mismatched id "${block.id}"`,
      };
    }

    if (!('type' in block) || !block.type || typeof block.type !== 'string') {
      return {
        success: false,
        error: `Block "${block.id}" must have a string "type" property`,
      };
    }

    if (
      !('config' in block) ||
      !block.config ||
      typeof block.config !== 'object'
    ) {
      return {
        success: false,
        error: `Block "${block.id}" must have a "config" object`,
      };
    }
  }

  // Validate connections structure
  for (let i = 0; i < dataObj.connections.length; i++) {
    const connection = dataObj.connections[i];

    if (typeof connection !== 'object' || connection === null) {
      return {
        success: false,
        error: `Connection at index ${i} must be an object`,
      };
    }

    const requiredFields = [
      'source_block_id',
      'source_port',
      'target_block_id',
      'target_port',
    ];
    for (const field of requiredFields) {
      if (!connection[field] || typeof connection[field] !== 'string') {
        return {
          success: false,
          error: `Connection at index ${i} must have a string "${field}" property`,
        };
      }
    }
  }

  return {
    success: true,
    data,
  };
}

/**
 * Check if block IDs in connections reference valid blocks
 */
export function validateConnectionReferences(
  data: Record<string, unknown>,
): ParseResult<Record<string, unknown>> {
  // blocks is now an object with block IDs as keys
  const blocksObj = data.blocks as Record<string, { id: string }>;
  const blockIds = new Set(Object.keys(blocksObj));

  const connections = data.connections as Array<{
    source_block_id: string;
    target_block_id: string;
  }>;
  for (let i = 0; i < connections.length; i++) {
    const connection = connections[i];

    if (!blockIds.has(connection.source_block_id)) {
      return {
        success: false,
        error: `Connection at index ${i} references non-existent source block "${connection.source_block_id}"`,
      };
    }

    if (!blockIds.has(connection.target_block_id)) {
      return {
        success: false,
        error: `Connection at index ${i} references non-existent target block "${connection.target_block_id}"`,
      };
    }
  }

  return {
    success: true,
    data,
  };
}

/**
 * Pretty-print YAML parsing errors for user display
 */
export function formatYamlError(error: string): string {
  // Clean up common error patterns for better user experience
  return error
    .replace(/^YAML syntax error: /, '')
    .replace(/YAMLException: /, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract line and column information from YAML error
 */
export function extractErrorLocation(error: string): {
  line?: number;
  column?: number;
} {
  const lineMatch = error.match(/line (\d+)/);
  const columnMatch = error.match(/column (\d+)/);

  return {
    line: lineMatch ? parseInt(lineMatch[1], 10) : undefined,
    column: columnMatch ? parseInt(columnMatch[1], 10) : undefined,
  };
}
